import type { QueryClient } from '@tanstack/react-query'
import type { EffectiveIdentity, SwapVerdict } from '@/lib/impersonation'
import { trpc } from '@/trpc/client'

/**
 * Re-read who the server is answering as, and report whether `hasTakenEffect`
 * holds of the answer.
 *
 * Shared by the start and stop impersonation hooks because the read is
 * identical for both and only the predicate differs — start asks "is the swap
 * live", stop asks "is it gone". They were written as one function and a
 * copy of it once, which is how `stop` came to have no verification at all
 * while `start` had a carefully argued one: nothing connected them, so
 * hardening one said nothing about the other.
 *
 * **The read goes through the shared QueryClient, not a bare `trpcClient`
 * call.** `fetchQuery` with `staleTime: 0` is just as fresh, and it does two
 * things a raw call cannot: the answer lands in the cache
 * `ImpersonationBanner` reads, so an identity that DID change is reflected in
 * place even when the caller declines to navigate; and it inherits the client's
 * retry policy, so a single blip does not decide the verdict.
 *
 * Why there are three verdicts rather than two is argued on `SwapVerdict` in
 * `@/lib/impersonation`, which is where the type lives.
 */
export async function verifyEffectiveIdentity(
  queryClient: QueryClient,
  hasTakenEffect: (me: EffectiveIdentity | null | undefined) => boolean,
): Promise<SwapVerdict> {
  const options = trpc.user.me.queryOptions()
  try {
    // Drop any `user.me` already in flight FIRST. `fetchQuery` joins an
    // existing request rather than starting one — four shell components hold
    // this key and `refetchOnWindowFocus` is on — and a request issued BEFORE
    // the cookie header landed answers with the PRE-change identity. Joining it
    // would read as `refused` on a change that is live, and would write that
    // stale identity into the cache the banner reads: exactly the stranded
    // state this verification exists to prevent.
    await queryClient.cancelQueries({ queryKey: options.queryKey })
    const me = await queryClient.fetchQuery({
      ...options,
      // Answer, never park. The client's default `networkMode: 'online'` makes
      // an offline fetch return a promise that NEVER settles — so the caller's
      // `onSuccess` would never return, the mutation would never leave
      // `isPending`, and the control stays disabled for the life of the page
      // with no toast at all. That is strictly worse than a wrong answer.
      // `'always'` makes the attempt fail instead, which lands in the catch
      // below and tells the admin something.
      networkMode: 'always',
      staleTime: 0,
      // Retries come from the shared client's `queries.retry`. `fetchQuery`
      // defaults `retry` to false when the option is undefined, so this is
      // inherited only because `makeQueryClient` sets one — a `defaultOptions`
      // edit would silently take it away.
    })
    return hasTakenEffect(me) ? 'applied' : 'refused'
  } catch {
    // Nothing is known — including whether the change is live. Deliberately NOT
    // narrowed into a cause: this catches a dead socket, a 429, a 500 and an
    // expired session alike, and every one of them has been mislabelled here at
    // least once.
    return 'unknown'
  }
}
